import {
  Container,
  Center,
  Loader,
  Title,
  Paper,
  Button,
  Text,
  NumberFormatter,
  Badge,
  Group,
  TextInput,
  Textarea,
  Grid,
  Stack,
  Switch,
  NumberInput,
  Autocomplete,
  ActionIcon,
  useComputedColorScheme,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import {
  checkout,
  customerPortal,
  getEnvironments,
  getTenantProfile,
  updateTenantProfile,
} from "../api";
import ApiKeys from "./ApiKeys";
import { notifications } from "@mantine/notifications";
import PricingPage from "./StripePlanCheckout";
import { useStore } from "../store";

const apiPrefix = import.meta.env.VITE_API_URL;
const authToken = useStore.getState().authToken;

/** API rates (0..1) → form rows with a whole percent. */
const toEnvironmentRows = (rates) =>
  (rates || []).map((r) => ({
    environment: r.environment,
    percent: Math.round(r.sampleRate * 100),
  }));

export default function TenantProfileEdit() {
  const [profile, setProfile] = useState(null);
  const [environmentNames, setEnvironmentNames] = useState([]);

  var theme = useComputedColorScheme();

  const form = useForm({
    initialValues: {
      name: "",
      // Textarea, one origin per line — the API takes a list.
      callbackOrigins: "",
      verificationEnabled: false,
      verifierModel: "",
      // Empty/null = follow the platform default (keep every run for the platform strip window).
      detailRetentionDays: null,
      detailSampleRatePercent: null,
      // [{ environment, percent }] — a higher rate for single environments.
      environmentSampleRates: [],
    },
    validate: {
      environmentSampleRates: {
        environment: (value) =>
          value && value.trim() ? null : "Enter an environment",
        percent: (value) =>
          value === "" || value == null ? "Enter a rate" : null,
      },
    },
  });

  useEffect(() => {
    getTenantProfile().then((data) => {
      var formInit = {
        name: data.name || "",
        callbackOrigins: (data.callbackOrigins || []).join("\n"),
        verificationEnabled: data.verificationEnabled || false,
        verifierModel: data.verifierModel || "",
        detailRetentionDays:
          data.detailRetentionDays == null ? null : data.detailRetentionDays,
        detailSampleRatePercent:
          data.detailSampleRate == null
            ? null
            : Math.round(data.detailSampleRate * 100),
        environmentSampleRates: toEnvironmentRows(
          data.environmentDetailSampleRates
        ),
      };
      form.setValues(formInit);
      setProfile(data);
    });
    getEnvironments()
      .then((envs) => setEnvironmentNames((envs || []).map((e) => e.name)))
      // Not fatal: the list only fills the environment picker.
      .catch(() => {});
    // Mantine form object has unstable identity; including it would rerun this effect every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!profile)
    return (
      <Container size="sm">
        <Center>
          <Loader />
        </Center>
      </Container>
    );

  return (
    <Container size="xl">
      <Title mb="md" order={1}>
        Tenant Profile
      </Title>

      <Grid>
        <Grid.Col span={6}>
          <Paper
            radius="md"
            p="lg"
            bg={theme == "dark" ? "dark" : "dark.0"}
            mb="xl"
            my="lg"
          >
            <Text mb="sm" fz="xl" fw={500}>
              Settings
            </Text>
            <form
              onSubmit={form.onSubmit((values) => {
                updateTenantProfile({
                  name: values.name,
                  callbackOrigins: values.callbackOrigins
                    .split("\n")
                    .map((o) => o.trim())
                    .filter(Boolean),
                  verificationEnabled: values.verificationEnabled,
                  verifierModel: values.verifierModel,
                  detailRetention: {
                    retentionDays:
                      values.detailRetentionDays == null ||
                      values.detailRetentionDays === ""
                        ? null
                        : Number(values.detailRetentionDays),
                    sampleRate:
                      values.detailSampleRatePercent == null ||
                      values.detailSampleRatePercent === ""
                        ? null
                        : Number(values.detailSampleRatePercent) / 100,
                    environmentSampleRates: values.environmentSampleRates.map(
                      (r) => ({
                        environment: r.environment.trim(),
                        sampleRate: Number(r.percent) / 100,
                      })
                    ),
                  },
                })
                  .then((data) => {
                    setProfile(data);
                    form.setFieldValue(
                      "callbackOrigins",
                      (data.callbackOrigins || []).join("\n")
                    );
                    form.setFieldValue(
                      "detailRetentionDays",
                      data.detailRetentionDays == null
                        ? null
                        : data.detailRetentionDays
                    );
                    form.setFieldValue(
                      "detailSampleRatePercent",
                      data.detailSampleRate == null
                        ? null
                        : Math.round(data.detailSampleRate * 100)
                    );
                    form.setFieldValue(
                      "environmentSampleRates",
                      toEnvironmentRows(data.environmentDetailSampleRates)
                    );
                    notifications.show({
                      title: "Profile updated",
                      message: "Your tenant profile has been updated",
                      color: "teal",
                    });
                  })
                  .catch((error) => {
                    notifications.show({
                      title: "An error occurred",
                      message: error,
                      color: "red",
                    });
                  });
              })}
            >
              <TextInput
                label="Organization Name"
                placeholder="Best Prompts, Inc."
                required
                {...form.getInputProps("name")}
              />

              <Textarea
                mt="md"
                label="Callback origins"
                placeholder={"https://app.example.com\n*.ngrok-free.dev\nlocalhost:5173"}
                autosize
                minRows={3}
                description="One per line. Hosts your apps may serve caller-hosted tools from — the origins accepted in the X-Shuttle-Callback-Url header. Scheme and port are optional; a leading *. matches sub-domains. Empty means caller-hosted tools do not run at all."
                {...form.getInputProps("callbackOrigins")}
              />

              <Switch
                mt="lg"
                label="Progress verification"
                description="Lets flows that ask for it be graded by a verifier model while they run. Each check is an extra inference billed to this tenant, so nothing is verified until both this and the flow's own setting are on. Turning this off stops all of it at once."
                {...form.getInputProps("verificationEnabled", { type: "checkbox" })}
              />

              {form.values.verificationEnabled && (
                <TextInput
                  mt="sm"
                  label="Verifier model"
                  placeholder="gpt-4o-mini (default)"
                  description="Leave empty for the default. Must be a model that returns token-level logprobs and does not think before answering — a reasoning model reports the same near-certainty whatever the evidence. Scores from different verifiers are not comparable."
                  {...form.getInputProps("verifierModel")}
                />
              )}

              <Text mt="xl" fz="lg" fw={500}>
                Request / response text
              </Text>
              <Text size="sm" c="dimmed" mb="sm">
                Cost, model, status and timing are always kept. These knobs
                only control how long — and for how many runs — the full prompt,
                answer text, and template parameter values are stored. Debug
                runs always keep full text. You cannot keep text longer than
                the platform window (
                {profile.platformDetailRetentionDays ?? 92} days).
              </Text>
              <NumberInput
                label="Keep full text for (days)"
                placeholder={`Platform default (${profile.platformDetailRetentionDays ?? 92})`}
                description="Leave empty to follow the platform default. Already-stored runs are stripped on the next hourly cleanup pass; large tenants may take several passes. The run row itself is kept for statistics."
                min={1}
                max={profile.platformDetailRetentionDays ?? 92}
                allowDecimal={false}
                {...form.getInputProps("detailRetentionDays")}
              />
              <NumberInput
                mt="md"
                label="Sample rate (%)"
                placeholder="100"
                description="Share of runs that persist full text. 100 keeps every run; 10 keeps about one in ten; 0 keeps none except debug runs. Decided when the run starts and is not retroactive."
                min={0}
                max={100}
                allowDecimal={false}
                suffix="%"
                {...form.getInputProps("detailSampleRatePercent")}
              />

              <Text mt="md" size="sm" fw={500}>
                Higher rates for single environments
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                For example, keep 100% of the runs in dev and demo. A run keeps
                its text when it falls in the highest rate that applies: the
                tenant rate above, the rate of its environment, or the rate of
                its flow (set in the flow settings). So a rate here can only
                keep more runs; a rate below the tenant rate has no effect.
              </Text>
              {form.values.environmentSampleRates.map((row, index) => {
                const taken = form.values.environmentSampleRates
                  .filter((_, i) => i !== index)
                  .map((r) => r.environment);
                const choices = [
                  ...new Set(
                    [...environmentNames, row.environment].filter(Boolean)
                  ),
                ].filter((name) => !taken.includes(name));
                return (
                  <Group key={index} align="flex-start" mb="xs">
                    {/* Free text with the tenant's environments as suggestions: a run's
                        environment need not have an environment record. */}
                    <Autocomplete
                      placeholder="Environment"
                      data={choices}
                      w={200}
                      {...form.getInputProps(
                        `environmentSampleRates.${index}.environment`
                      )}
                    />
                    <NumberInput
                      placeholder="100"
                      min={0}
                      max={100}
                      allowDecimal={false}
                      suffix="%"
                      w={120}
                      {...form.getInputProps(
                        `environmentSampleRates.${index}.percent`
                      )}
                    />
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      mt={4}
                      aria-label="Remove environment rate"
                      onClick={() =>
                        form.removeListItem("environmentSampleRates", index)
                      }
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                );
              })}
              <Button
                variant="subtle"
                size="xs"
                onClick={() =>
                  form.insertListItem("environmentSampleRates", {
                    environment: "",
                    percent: 100,
                  })
                }
              >
                Add environment rate
              </Button>

              <Group mt="md" justify="flex-end">
                <Button variant="outline" type="submit">
                  Save
                </Button>
              </Group>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={6}>
          <Paper
            radius="md"
            p="lg"
            bg={theme == "dark" ? "dark" : "dark.0"}
            mb="xl"
            my="lg"
          >
            <Text mb="sm" fz="xl" fw={500}>
              Credits
            </Text>
            <Stack>
              <Group justify="space-between">
                <Badge radius="xs" size="xl" variant="dot" color="grape">
                  <NumberFormatter
                    value={profile.credits}
                    decimalScale={0}
                    thousandSeparator
                  />
                </Badge>
                <Text fz="lg">
                  &cong;
                  <NumberFormatter
                    value={profile.credits / 1000000}
                    decimalScale={2}
                    thousandSeparator
                    suffix="$"
                  />
                </Text>

                <Button
                  onClick={() => {
                    checkout({})
                      .then((data) => {
                        location.href = data.redirectUrl;
                      })
                      .catch((error) => {
                        alert("Oh no, something went wrong: " + error);
                      });
                  }}
                >
                  Buy Credits
                </Button>
              </Group>

              <Group mt="xl" justify="space-between">
                <Text>Your plan: {profile.plan}</Text>
                {profile.plan !== "Usage-Based" && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      customerPortal().then((data) => {
                        window.location = data.redirectUrl;
                      });
                    }}
                  >
                    Manage Subscription
                  </Button>
                )}
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        {(!profile.plan || profile.plan == "Usage-Based") && (
          <Grid.Col span={12}>
            <PricingPage />
          </Grid.Col>
        )}

        <Grid.Col span={12}>
          <Paper withBorder mt="xl" p="lg" radius="md">
            <ApiKeys />
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper withBorder mt="xl" p="lg" radius="md">
            <Text mb="sm" fz="xl" fw={500}>
              Export Data
            </Text>
            <Button
              component="a"
              href={`${apiPrefix}/export?token=${authToken}`}
            >
              Export all Prompts
            </Button>
          </Paper>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
